// =============================================================================
// TENANT PLAN HISTORY — timeline of a tenant's Platform-tier plan stays
//
// Shown on the super-admin TenantDetail Overview tab. Reads
// tenant_plan_history (Platform DB), which is populated automatically by a
// trigger on every change to tenants.plan_id / status. Each row snapshots
// the plan name + price at the moment of the change so history is honest
// even after later plan repricing.
// =============================================================================

import { useEffect, useState } from 'react';
import { Loader2, Download, ArrowDown, ArrowUp, X as XIcon, Sparkles, RefreshCw, Settings as SettingsIcon, Unlock, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { resetTenantStarterEligibility } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface HistoryRow {
  id:                       string;
  plan_id:                  string | null;
  plan_name:                string | null;
  display_name:             string | null;
  price_per_seat_monthly:   number | null;
  price_per_seat_annual:    number | null;
  is_free:                  boolean;
  status:                   string;
  change_reason:             string;
  started_at:                string;
  ended_at:                  string | null;
}

const REASONS: Record<string, { label: string; icon: typeof ArrowDown; cls: string }> = {
  signup:       { label: 'Provisioned',   icon: Sparkles,    cls: 'text-blue-600' },
  upgrade:      { label: 'Upgraded',      icon: ArrowUp,     cls: 'text-emerald-600' },
  downgrade:    { label: 'Downgraded',    icon: ArrowDown,   cls: 'text-amber-600' },
  cancel:       { label: 'Cancelled',     icon: XIcon,       cls: 'text-red-600' },
  reactivate:   { label: 'Reactivated',   icon: RefreshCw,   cls: 'text-emerald-600' },
  admin_change: { label: 'Plan change',   icon: SettingsIcon, cls: 'text-surface-500' },
  trial_end:    { label: 'Trial ended',   icon: SettingsIcon, cls: 'text-surface-500' },
};

export function TenantPlanHistory({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const { isSuperAdmin } = useAuth();
  const [rows, setRows]     = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // ── Starter reset state ────────────────────────────────────────────
  // Pulled from the tenants row on mount so we know whether the
  // tenant has used Starter — drives the "Reset eligibility" button.
  // Re-fetched after a successful reset so the button hides.
  const [starterUsedAt, setStarterUsedAt] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const fetchStarterStamp = async () => {
    const { data } = await supabase
      .from('tenants')
      .select('starter_plan_used_at')
      .eq('id', tenantId)
      .maybeSingle();
    setStarterUsedAt((data as any)?.starter_plan_used_at ?? null);
  };

  const handleResetStarter = async () => {
    setResetting(true);
    setResetError(null);
    const result = await resetTenantStarterEligibility(tenantId);
    setResetting(false);
    if (!result.ok) {
      setResetError(result.error || 'Reset failed');
      return;
    }
    setResetSuccess(true);
    setShowResetConfirm(false);
    setStarterUsedAt(null);
    // Auto-hide success after a few seconds.
    setTimeout(() => setResetSuccess(false), 4000);
  };

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      const [{ data, error: e }] = await Promise.all([
        supabase
          .from('tenant_plan_history')
          .select('id, plan_id, plan_name, display_name, price_per_seat_monthly, price_per_seat_annual, is_free, status, change_reason, started_at, ended_at')
          .eq('tenant_id', tenantId)
          .order('started_at', { ascending: false }),
        fetchStarterStamp(),
      ]);

      if (!alive) return;
      if (e) {
        console.error('[TenantPlanHistory] fetch error:', e);
        setError(e.message);
      } else {
        setRows((data ?? []) as HistoryRow[]);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = [
      'Plan', 'Display name', 'Monthly (cents)', 'Annual (cents)',
      'Status', 'Change', 'Started', 'Ended',
    ];
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = rows.map(r => [
      r.plan_name ?? '—',
      r.display_name ?? '',
      r.price_per_seat_monthly ?? '',
      r.price_per_seat_annual ?? '',
      r.status,
      REASONS[r.change_reason]?.label ?? r.change_reason,
      r.started_at,
      r.ended_at ?? '',
    ].map(escape).join(','));
    const csv  = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${tenantName.replace(/\s+/g, '-').toLowerCase()}-plan-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="card p-4 flex items-center gap-2 text-sm text-surface-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading plan history…
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-4 text-sm text-red-700">Couldn't load plan history: {error}</div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="card p-4 text-sm text-surface-500">No plan history yet.</div>
    );
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-surface-900">Plan History</h3>
        <div className="flex items-center gap-3">
          {/* Super-admin only: re-enable Starter for this tenant when
              starter_plan_used_at is set. Hidden when the tenant has
              never used Starter (button would be a no-op). */}
          {isSuperAdmin && starterUsedAt && (
            <button
              onClick={() => { setResetError(null); setShowResetConfirm(true); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:underline"
              title={`Starter used ${new Date(starterUsedAt).toLocaleDateString()} — click to re-enable`}
            >
              <Unlock className="w-3.5 h-3.5" /> Reset Starter eligibility
            </button>
          )}
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Success toast — auto-hides after 4s */}
      {resetSuccess && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
          <Unlock className="w-3.5 h-3.5" />
          Starter eligibility re-enabled. The tenant can now downgrade back to Starter.
        </div>
      )}

      {/* Confirmation modal — kept inline so we don't need to import a Modal
          dependency just for one screen. Click outside cancels via the backdrop. */}
      {showResetConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !resetting && setShowResetConfirm(false)}
        >
          <div
            className="bg-surface-0 dark:bg-surface-100 rounded-xl shadow-xl max-w-md w-full p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-surface-900">Reset Starter eligibility?</h4>
                <p className="text-xs text-surface-600 mt-1">
                  This will clear <span className="font-mono">starter_plan_used_at</span> for{' '}
                  <span className="font-semibold">{tenantName}</span>, allowing them to downgrade back to the Starter plan.
                  {starterUsedAt && (
                    <>
                      {' '}They originally used Starter on{' '}
                      <span className="font-medium">{new Date(starterUsedAt).toLocaleDateString()}</span>.
                    </>
                  )}
                </p>
                <p className="text-[11px] text-surface-500 mt-2">
                  This action is logged in <span className="font-mono">tenant_plan_history</span> via the
                  next plan change. Only super admins can do this.
                </p>
              </div>
            </div>

            {resetError && (
              <div className="p-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                {resetError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-surface-200">
              <button
                onClick={() => setShowResetConfirm(false)}
                disabled={resetting}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-surface-700 hover:bg-surface-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetStarter}
                disabled={resetting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                {resetting ? 'Resetting…' : 'Confirm reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ol className="relative pl-5 space-y-3">
        <span className="absolute left-1.5 top-1.5 bottom-1.5 w-px bg-surface-200" aria-hidden />
        {rows.map((r) => {
          const reason = REASONS[r.change_reason] ?? REASONS.admin_change;
          const Icon = reason.icon;
          return (
            <li key={r.id} className="relative">
              <span
                className={`absolute -left-3.5 top-1 w-2.5 h-2.5 rounded-full ring-2 ring-surface-0 ${
                  r.ended_at == null ? 'bg-emerald-500' : 'bg-surface-300'
                }`}
                aria-hidden
              />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-surface-900">
                      {r.display_name ?? r.plan_name ?? 'Unknown plan'}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${reason.cls}`}>
                      <Icon className="w-3 h-3" /> {reason.label}
                    </span>
                    {r.is_free && (
                      <span className="text-[10px] font-medium text-surface-600 bg-surface-100 px-1.5 py-0.5 rounded">
                        FREE
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-surface-500 mt-0.5">
                    {new Date(r.started_at).toLocaleDateString()} →{' '}
                    {r.ended_at
                      ? new Date(r.ended_at).toLocaleDateString()
                      : <span className="text-emerald-700 font-medium">Present</span>}
                    <span className="ml-2 capitalize text-surface-400">· {r.status}</span>
                  </p>
                </div>
                {r.price_per_seat_monthly != null && r.price_per_seat_monthly > 0 && (
                  <span className="text-xs font-medium text-surface-700 shrink-0">
                    ${(r.price_per_seat_monthly / 100).toFixed(0)}/seat/mo
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
